import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { ArrowLeft, Plus, Filter, Pencil, Trash2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { getInstamojoSession, clearInstamojoSession } from './InstamojoPasswordGate';

const PAGE_SIZE = 20;

const fmtINR = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Transaction-date cells show full date+time in IST, 24-hour, e.g. "02/05/2026 16:20:46".
const DT_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone:  'Asia/Kolkata',
  day:       '2-digit',
  month:     '2-digit',
  year:      'numeric',
  hour:      '2-digit',
  minute:    '2-digit',
  second:    '2-digit',
  hour12:    false,
});
const fmtDateTime = (s) => {
  if (!s) return '—';
  const raw = String(s);
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
  const dt = new Date(hasTz ? raw : raw + 'Z');
  if (isNaN(dt.getTime())) return raw;
  return DT_FMT.format(dt).replace(', ', ' ');
};

export default function InstamojoPayments() {
  const navigate = useNavigate();

  // Filter form state (what's in the inputs right now)
  const [titleId, setTitleId] = useState(0);
  const [titleText, setTitleText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');

  // Applied filters (what the API actually sees). Purpose pick auto-applies;
  // dates only apply when the Filter button is clicked. Matches Rotary 1:1.
  const [applied, setApplied] = useState({ titleId: 0, from: '', to: '' });

  // Purpose autocomplete
  const [acOpen, setAcOpen]   = useState(false);
  const [acItems, setAcItems] = useState([]);
  const acTimer = useRef(null);

  // Dashboard + grid
  const [loading, setLoading] = useState(true);
  const [dash, setDash]       = useState(null);
  const [page, setPage]       = useState(1);
  const [grid, setGrid]       = useState({ total: 0, total_pages: 0, results: [] });

  // Show Profit
  const [profitDialogOpen, setProfitDialogOpen] = useState(false);
  const [profitPwd, setProfitPwd]               = useState('');
  const [profitVerifying, setProfitVerifying]   = useState(false);
  const [profitVisible, setProfitVisible]       = useState(false);
  const [profitValue, setProfitValue]           = useState(0);

  // OTP delete
  const [deletingRow, setDeletingRow]   = useState(null);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otpInput, setOtpInput]         = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpRequesting, setOtpRequesting] = useState(false);
  const [otpRecipient, setOtpRecipient]   = useState('');

  // Session check
  useEffect(() => {
    if (!getInstamojoSession()) navigate('/instamojo', { replace: true });
  }, [navigate]);

  const buildParams = useCallback(() => {
    const p = {};
    if (applied.titleId) p.title_id = applied.titleId;
    if (applied.from)    p.from = applied.from;
    if (applied.to)      p.to = applied.to;
    return p;
  }, [applied]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/instamojo-archive/payments/dashboard', { params: buildParams() });
      setDash(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        clearInstamojoSession();
        toast.error('Session expired.');
        navigate('/instamojo', { replace: true });
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to load dashboard.');
    }
  }, [buildParams, navigate]);

  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/instamojo-archive/payments', {
        params: { ...buildParams(), page, size: PAGE_SIZE },
      });
      setGrid(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        clearInstamojoSession();
        toast.error('Session expired.');
        navigate('/instamojo', { replace: true });
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to load payments.');
    } finally {
      setLoading(false);
    }
  }, [buildParams, page, navigate]);

  useEffect(() => { fetchDashboard(); fetchGrid(); }, [fetchDashboard, fetchGrid]);

  // Reset profit reveal whenever the APPLIED filter scope changes
  useEffect(() => { setProfitVisible(false); setProfitValue(0); }, [applied]);

  // ─── Purpose autocomplete ────────────────────────────────────────────────
  const onTitleChange = (e) => {
    const v = e.target.value;
    setTitleText(v);
    setTitleId(0);
    if (acTimer.current) clearTimeout(acTimer.current);
    if (v.trim().length < 3) { setAcOpen(false); setAcItems([]); return; }
    acTimer.current = setTimeout(async () => {
      try {
        const res = await api.get('/instamojo-archive/purposes/autocomplete', {
          params: { q: v.trim(), limit: 10 },
        });
        setAcItems(res.data || []);
        setAcOpen(true);
      } catch {/* ignore */}
    }, 250);
  };
  const pickTitle = (item) => {
    setTitleText(item.title || '');
    setTitleId(item.id);
    setAcOpen(false);
    setPage(1);
    // Auto-apply on Purpose pick (Rotary's fncsave behavior). Carry whatever
    // dates have already been applied so we don't accidentally drop them.
    setApplied((a) => ({ ...a, titleId: item.id }));
  };
  const clearTitle = () => {
    setTitleText('');
    setTitleId(0);
    setAcOpen(false);
    setPage(1);
    setApplied((a) => ({ ...a, titleId: 0 }));
  };

  // Filter button — only point at which dates are applied.
  const applyFilter = () => {
    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      toast.error('Please select both From Date and To Date.');
      return;
    }
    setPage(1);
    setApplied({ titleId, from: fromDate, to: toDate });
  };

  // ─── Show Profit ──────────────────────────────────────────────────────────
  const openProfit = () => {
    setProfitPwd('');
    setProfitDialogOpen(true);
  };
  const validateProfit = async () => {
    if (!profitPwd) { toast.error('Enter password.'); return; }
    setProfitVerifying(true);
    try {
      const res = await api.post('/instamojo-archive/show-profit', {
        password: profitPwd,
        from:     applied.from    || null,
        to:       applied.to      || null,
        title_id: applied.titleId || null,
      });
      setProfitValue(res.data.profit ?? 0);
      setProfitVisible(true);
      setProfitDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wrong Password');
    } finally {
      setProfitVerifying(false);
    }
  };

  // ─── OTP delete ───────────────────────────────────────────────────────────
  const startDelete = async (row) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    setDeletingRow(row);
    setOtpRequesting(true);
    try {
      const res = await api.post(`/instamojo-archive/payments/${row.id}/request-delete-otp`);
      setOtpRecipient(res.data?.recipient_masked || '');
      // Dev-mode only: backend may return OTP in response. Pre-fill so testing doesn't need email.
      if (res.data?.dev_otp) {
        setOtpInput(res.data.dev_otp);
        toast.info('Dev mode: OTP filled in for testing.');
      } else {
        setOtpInput('');
        if (res.data?.emailed) toast.success('OTP sent — check the configured inbox.');
        else                   toast.warning('OTP generated but email could not be sent. Check API logs.');
      }
      setOtpDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP.');
      setDeletingRow(null);
    } finally {
      setOtpRequesting(false);
    }
  };
  const verifyAndDelete = async () => {
    if (!deletingRow || !otpInput.trim()) { toast.error('Enter OTP.'); return; }
    setOtpVerifying(true);
    try {
      await api.post(`/instamojo-archive/payments/${deletingRow.id}/confirm-delete`, {
        otp: otpInput.trim(),
      });
      toast.success('Payment Deleted Successfully');
      setOtpDialogOpen(false);
      setDeletingRow(null);
      setOtpInput('');
      fetchDashboard();
      fetchGrid();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setOtpVerifying(false);
    }
  };
  const cancelOtp = () => {
    setOtpDialogOpen(false);
    setDeletingRow(null);
    setOtpInput('');
    setOtpRecipient('');
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  const totalPages = grid.total_pages || 1;
  const startIdx = grid.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx   = Math.min(page * PAGE_SIZE, grid.total);

  const Stat = ({ label, value, accent = 'slate' }) => (
    <Card className={`p-3 text-center bg-white border-${accent}-200`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-xl font-bold text-${accent}-700`}>{fmtINR(value)}</div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-4">
      <div className="w-full space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigate('/instamojo/list')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Instamojo Payments</h1>
          </div>
          <Button onClick={() => navigate('/instamojo/payment/new')}>
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2 relative">
              <Label>Search Purpose</Label>
              <div className="flex gap-2">
                <Input value={titleText}
                  onChange={onTitleChange}
                  onFocus={() => acItems.length && setAcOpen(true)}
                  onBlur={() => setTimeout(() => setAcOpen(false), 150)}
                  placeholder="Enter Purpose (min 3 chars)" />
                {titleText && (
                  <Button variant="outline" size="icon" onClick={clearTitle} title="Clear">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {acOpen && acItems.length > 0 && (
                <div className="absolute z-20 mt-1 w-[calc(100%-3rem)] bg-white border border-slate-200 rounded-md shadow max-h-64 overflow-y-auto">
                  {acItems.map((it) => (
                    <button key={it.id}
                      type="button"
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-violet-50"
                      onMouseDown={(e) => { e.preventDefault(); pickTitle(it); }}>
                      {it.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>From Date</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div>
              <Label>To Date</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
          <div className="mt-3">
            <Button onClick={applyFilter}>
              <Filter className="w-4 h-4 mr-1" /> Filter
            </Button>
          </div>
        </Card>

        {/* Instamojo strip — 5 cards */}
        <Card className="p-4">
          <div className="text-lg font-bold text-slate-900 mb-3">Instamojo</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Total Collection"     value={dash?.total_collection}     accent="sky" />
            <Stat label="Instamojo Commission" value={dash?.instamojo_commission} accent="amber" />
            <Stat label="Receivable"           value={dash?.receivable}           accent="emerald" />
            <Stat label="Received"             value={dash?.received}             accent="teal" />
            <Stat label="Balance"              value={dash?.balance}              accent="rose" />
          </div>
        </Card>

        {/* Customer Payout (left) + Kaizen (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Customer Payout — 3 stacked */}
          <Card className="p-4">
            <div className="text-lg font-bold text-slate-900 mb-3">Customer Payout</div>
            <div className="space-y-3">
              <Stat label="Total Customer Payout"   value={dash?.total_customer_payout}   accent="indigo" />
              <Stat label="Settled Customer Payout" value={dash?.settled_customer_payout} accent="violet" />
              <Stat label="Pending Customer Payout" value={dash?.pending_customer_payout} accent="orange" />
            </div>
          </Card>

          {/* Kaizen — 3 horizontal */}
          <Card className="p-4">
            <div className="text-lg font-bold text-slate-900 mb-3">Kaizen</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Stat label="Paid"    value={dash?.paid}           accent="emerald" />
              <Stat label="Balance" value={dash?.kaizen_balance} accent="amber" />
              {/* Profit cell — gated by Show Profit password */}
              <Card className="p-3 text-center bg-white border-fuchsia-200">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Profit</div>
                {profitVisible ? (
                  <div className="mt-1 text-xl font-bold text-fuchsia-700">{fmtINR(profitValue)}</div>
                ) : (
                  <button type="button"
                    onClick={openProfit}
                    className="mt-1 text-sm font-semibold text-fuchsia-700 underline-offset-2 hover:underline">
                    Show Profit
                  </button>
                )}
              </Card>
            </div>
          </Card>
        </div>

        {/* Grid */}
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm table-fixed border-collapse
                         [&_th]:border [&_th]:border-slate-300 [&_th]:px-2 [&_th]:py-2 [&_th]:font-semibold [&_th]:text-slate-700
                         [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-2 [&_td]:align-middle
                         [&_tbody_tr:nth-child(even)]:bg-slate-50 [&_tbody_tr:hover]:bg-violet-50/60">
              <colgroup>
                <col style={{ width: '6%'  }} />   {/* Sr. No. */}
                <col style={{ width: '22%' }} />   {/* Title */}
                <col style={{ width: '11%' }} />   {/* Amount */}
                <col style={{ width: '14%' }} />   {/* Transaction Date */}
                <col style={{ width: '24%' }} />   {/* Bank Details */}
                <col style={{ width: '15%' }} />   {/* Remark */}
                <col style={{ width: '4%'  }} />   {/* Edit */}
                <col style={{ width: '4%'  }} />   {/* Delete */}
              </colgroup>
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th>Sr. No.</th>
                  <th>Title</th>
                  <th>Amount</th>
                  <th>Transaction Date</th>
                  <th>Bank Details</th>
                  <th>Remark</th>
                  <th className="!text-center">Edit</th>
                  <th className="!text-center">Delete</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="!text-center !py-6 text-slate-500">
                    <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && grid.results.length === 0 && (
                  <tr><td colSpan={8} className="!text-center !py-6 text-slate-500">No payment records found.</td></tr>
                )}
                {!loading && grid.results.map((row, i) => (
                  <tr key={row.id}>
                    <td className="text-slate-600">{startIdx + i}</td>
                    <td className="truncate" title={row.title || ''}>{row.title || '—'}</td>
                    <td className="text-right tabular-nums">{fmtINR(row.amount)}</td>
                    <td title={row.created_date || ''}>{fmtDateTime(row.created_date)}</td>
                    <td className="truncate" title={row.bank_details || ''}>{row.bank_details || '—'}</td>
                    <td className="truncate" title={row.remark || ''}>{row.remark || '—'}</td>
                    <td className="text-center">
                      <button type="button"
                        onClick={() => navigate(`/instamojo/payment/${row.id}/edit`)}
                        title="Edit"
                        className="p-1 rounded hover:bg-slate-200 text-slate-600">
                        <Pencil className="w-4 h-4 inline" />
                      </button>
                    </td>
                    <td className="text-center">
                      <button type="button"
                        onClick={() => startDelete(row)}
                        disabled={otpRequesting}
                        title="Delete"
                        className="p-1 rounded hover:bg-red-100 text-red-600 disabled:opacity-50">
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between flex-wrap gap-2 p-3 border-t bg-slate-50 text-sm">
            <div className="text-slate-600">
              Showing <span className="font-medium">{startIdx}</span>–<span className="font-medium">{endIdx}</span> of{' '}
              <span className="font-medium">{(grid.total || 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(1)}>«</Button>
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</Button>
              <span className="px-2">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Show Profit password modal */}
      <Dialog open={profitDialogOpen} onOpenChange={(o) => !o && setProfitDialogOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enter Password</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input type="password" value={profitPwd}
              onChange={(e) => setProfitPwd(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && validateProfit()}
              placeholder="Profit password" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfitDialogOpen(false)} disabled={profitVerifying}>Cancel</Button>
            <Button onClick={validateProfit} disabled={profitVerifying}>
              {profitVerifying ? 'Validating…' : 'Validate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OTP delete modal */}
      <Dialog open={otpDialogOpen} onOpenChange={(o) => { if (!o) cancelOtp(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enter OTP to Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <Input value={otpInput}
              onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && verifyAndDelete()}
              placeholder="6-digit OTP" inputMode="numeric" maxLength={6} autoFocus />
            <div className="text-xs text-slate-500">
              {otpRecipient
                ? <>OTP emailed to <span className="font-medium">{otpRecipient}</span>. Valid for 10 minutes.</>
                : <>An OTP has been generated. Valid for 10 minutes.</>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelOtp} disabled={otpVerifying}>Cancel</Button>
            <Button onClick={verifyAndDelete} disabled={otpVerifying}
              className="bg-red-600 hover:bg-red-700">
              {otpVerifying ? 'Deleting…' : 'Verify & Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
